import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Collapse, IconButton, Slide, Typography } from '@mui/material';

interface TopBannerProps {
  open: boolean;
  bgcolor?: string;
  icon?: React.ReactNode;
  message: React.ReactNode;
  actionLabel?: string;
  actionDisabled?: boolean;
  onAction?: () => void;
  onClose: () => void;
  inline?: boolean;
  align?: 'center' | 'space-between';
}

const TopBanner = ({
  open,
  bgcolor = 'primary.main',
  icon = undefined,
  message,
  actionLabel = undefined,
  actionDisabled = false,
  onAction = undefined,
  onClose,
  inline = false,
  align = 'center',
}: TopBannerProps): JSX.Element => {
  const isCentered = align === 'center';
  const banner = (
    <Box
      sx={{
        ...(inline
          ? {}
          : {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2000,
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            }),
        bgcolor,
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.25,
        minHeight: 30,
      }}
    >
      {/* Mirrors the actions width so the message stays centered */}
      {isCentered && <Box sx={{ flex: 1 }} />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ display: { xs: 'none', sm: 'flex' }, '& svg': { fontSize: 16 } }}>{icon}</Box>}
        <Typography
          variant="body2"
          sx={{
            color: 'inherit',
            fontSize: { xs: '12px', sm: '13px' },
            fontWeight: 400,
            textAlign: isCentered ? 'center' : 'left',
          }}
        >
          {message}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
        {actionLabel && onAction && (
          <Button
            size="small"
            variant="contained"
            onClick={onAction}
            disabled={actionDisabled}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'inherit',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: 1.5,
              px: 1.5,
              py: 0,
              minHeight: 22,
              borderRadius: '3px',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
              '&:disabled': { color: 'inherit', opacity: 0.65 },
            }}
          >
            {actionLabel}
          </Button>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit', p: 0.5 }} aria-label="Cerrar">
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );

  if (inline) {
    return (
      <Collapse in={open} unmountOnExit sx={{ flexShrink: 0 }}>
        {banner}
      </Collapse>
    );
  }

  return (
    <Slide direction="down" in={open} mountOnEnter unmountOnExit>
      {banner}
    </Slide>
  );
};

export default TopBanner;
